import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Download, CheckCircle2, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { EXAMPLES } from "@/data/examples";

/**
 * Landing-page "Real Output Examples" trust section.
 * Placed inside the Full Output Inventory section, just before the
 * "This is not just content. This is how you grow." closing block.
 *
 * Since we have ONE flagship example at launch, this renders a SINGLE
 * featured card (not a weak 3-column grid). Adding more examples will
 * automatically switch to a responsive grid layout.
 */
const RealOutputExamplesSection = () => {
  const isMultiple = EXAMPLES.length > 1;

  return (
    <div
      className="mt-24 md:mt-28 pt-16 md:pt-20 border-t border-neutral-800/60"
      data-testid="examples-landing-section"
    >
      <div className="text-center mb-12 md:mb-14">
        <motion.p
          className="text-secondary uppercase text-xs tracking-widest mb-4 font-semibold"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
        >
          Real Output Examples
        </motion.p>
        <motion.h2
          className="text-3xl md:text-4xl font-unbounded font-bold mb-4"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          See exactly what one processed episode becomes
        </motion.h2>
        <motion.p
          className="text-neutral-400 max-w-2xl mx-auto"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
        >
          Open a real EpisodeKit deliverable and inspect the exact transcript,
          show notes, blog post, social copy, quotes, SEO assets, and YouTube
          outputs generated from one upload.
        </motion.p>
      </div>

      <div
        className={
          isMultiple
            ? "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto"
            : "max-w-4xl mx-auto"
        }
      >
        {EXAMPLES.map((ex, i) => (
          <ExampleCard key={ex.slug} example={ex} featured={!isMultiple} index={i} />
        ))}
      </div>
    </div>
  );
};

const ExampleCard = ({ example, featured, index }) => {
  const detailHref = `/episode-examples/${example.slug}`;

  return (
    <motion.article
      data-testid={`example-card-${example.slug}`}
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay: index * 0.08 }}
      className="group relative rounded-2xl bg-gradient-to-b from-[#15131F] to-[#0E0D17] border border-neutral-800/80 hover:border-purple-500/40 transition-all duration-300 overflow-hidden"
      style={{
        boxShadow: "0 0 0 1px rgba(124,58,237,0.04), 0 30px 80px -40px rgba(124,58,237,0.35)",
      }}
    >
      {/* Soft purple glow halo */}
      <div className="pointer-events-none absolute -top-24 -right-20 w-72 h-72 rounded-full bg-purple-600/15 blur-[80px]" />
      <div className="pointer-events-none absolute -bottom-24 -left-20 w-72 h-72 rounded-full bg-purple-700/10 blur-[80px]" />

      <Link
        to={detailHref}
        className="block"
        data-testid={`example-card-link-${example.slug}`}
      >
        <div className={featured ? "md:grid md:grid-cols-[1.05fr_1fr] md:gap-0" : ""}>
          {/* Cover preview */}
          <div className="relative bg-[#0A0913] p-6 md:p-8 flex items-center justify-center">
            <div className="relative w-full max-w-md">
              {/* Stacked background sheets for "real deliverable" depth */}
              <div className="absolute inset-0 translate-x-3 translate-y-3 rounded-lg bg-purple-500/10 border border-purple-500/15" aria-hidden="true" />
              <div className="absolute inset-0 translate-x-1.5 translate-y-1.5 rounded-lg bg-neutral-800/60 border border-neutral-700/60" aria-hidden="true" />
              <img
                src={example.coverImage}
                alt={`Cover page of the EpisodeKit deliverable for ${example.title}`}
                loading="lazy"
                className="relative w-full rounded-lg shadow-2xl ring-1 ring-purple-500/10 group-hover:scale-[1.015] transition-transform duration-500"
              />
              <div className="absolute top-3 left-3 px-2.5 py-1 rounded-md bg-purple-600/90 backdrop-blur text-[10px] tracking-widest uppercase font-semibold text-white">
                Real Deliverable
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="p-6 md:p-8 flex flex-col">
            <div className="flex items-center gap-2 text-xs text-purple-300 uppercase tracking-widest font-semibold mb-3">
              <FileText className="w-3.5 h-3.5" />
              <span>Source · {example.sourcePodcast}</span>
            </div>

            <h3 className="text-2xl md:text-[28px] font-unbounded font-bold text-white leading-tight mb-3">
              {example.title}
            </h3>

            <p className="text-neutral-400 text-sm md:text-base leading-relaxed mb-5">
              Inspect the full transcript, editorial show notes, SEO blog post,
              and 12+ platform-tuned outputs — all generated from one upload.
            </p>

            {/* Metadata chips */}
            <div className="flex flex-wrap gap-2 mb-5">
              {Object.values(example.meta).map((m) => (
                <span
                  key={m}
                  className="px-2.5 py-1 rounded-full text-[11px] font-medium text-neutral-300 bg-neutral-800/70 border border-neutral-700/60"
                >
                  {m}
                </span>
              ))}
            </div>

            {/* Highlighted outputs */}
            <ul className="grid grid-cols-2 gap-x-3 gap-y-2 mb-7">
              {example.landingHighlights.map((output) => (
                <li
                  key={output}
                  className="flex items-center gap-2 text-sm text-neutral-300"
                >
                  <CheckCircle2 className="w-4 h-4 text-purple-400 flex-shrink-0" />
                  <span>{output}</span>
                </li>
              ))}
            </ul>

            {/* CTAs */}
            <div className="mt-auto flex flex-wrap items-center gap-3">
              <span
                className="btn-primary inline-flex items-center gap-2 group-hover:!scale-[1.03]"
                data-testid={`example-preview-cta-${example.slug}`}
              >
                Preview Example
                <ArrowRight className="w-4 h-4" />
              </span>
              <a
                href={example.pdfUrl}
                download
                onClick={(e) => e.stopPropagation()}
                className="inline-flex items-center gap-2 text-sm font-medium text-neutral-300 hover:text-white border border-neutral-700 hover:border-purple-500/60 rounded-full px-4 py-2.5 transition-colors"
                data-testid={`example-download-cta-${example.slug}`}
              >
                <Download className="w-4 h-4" />
                Download PDF
              </a>
              <span className="text-xs text-neutral-500 italic">
                Generated from a real uploaded episode
              </span>
            </div>
          </div>
        </div>
      </Link>
    </motion.article>
  );
};

export default RealOutputExamplesSection;
